/* eslint-disable no-console -- this is THE logger; console is the underlying sink */
/**
 * Shared logger primitive. Each app/function wraps this to enrich with
 * app-specific context (tenantId, requestId, userId, etc.).
 *
 * Levels: debug | info | warn | error.
 * In test mode (`process.env.NODE_ENV === 'test'`), all levels are suppressed by default.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogContext = Readonly<Record<string, unknown>>;

export interface Logger {
  debug(msg: string, ctx?: LogContext): void;
  info(msg: string, ctx?: LogContext): void;
  warn(msg: string, ctx?: LogContext): void;
  error(msg: string, ctx?: LogContext): void;
  child(extraCtx: LogContext): Logger;
}

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

export interface LoggerOptions {
  readonly app: string;
  readonly minLevel?: LogLevel;
  readonly baseContext?: LogContext;
  readonly suppressed?: boolean;
}

export function createLogger(opts: LoggerOptions): Logger {
  const minLevelValue = LEVEL_ORDER[opts.minLevel ?? 'info'];
  const baseContext = { app: opts.app, ...opts.baseContext };

  function emit(level: LogLevel, msg: string, ctx?: LogContext): void {
    if (opts.suppressed) return;
    if (LEVEL_ORDER[level] < minLevelValue) return;
    const payload = { level, msg, ts: new Date().toISOString(), ...baseContext, ...ctx };
    const out = JSON.stringify(payload);
    if (level === 'error') console.error(out);
    else if (level === 'warn') console.warn(out);
    else console.log(out);
  }

  return {
    debug: (msg, ctx) => emit('debug', msg, ctx),
    info: (msg, ctx) => emit('info', msg, ctx),
    warn: (msg, ctx) => emit('warn', msg, ctx),
    error: (msg, ctx) => emit('error', msg, ctx),
    child: (extraCtx) =>
      createLogger({
        app: opts.app,
        minLevel: opts.minLevel,
        suppressed: opts.suppressed,
        baseContext: { ...baseContext, ...extraCtx },
      }),
  };
}
